import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { GoogleMap, MarkerF, PolylineF, CircleF, useLoadScript } from "@react-google-maps/api";
import { useAuth } from "@/contexts/AuthContext";
import { apiFetch } from "@/lib/api-fetch";
import {
  ArrowLeft,
  CircleDot,
  Crosshair,
  Loader2,
  MousePointer2,
  Navigation,
  Route,
  Save,
} from "lucide-react";

type Point = { lat: number; lng: number };
type GpsPoint = Point & { accuracy: number | null };

type Windrow = {
  id: number;
  row_id: string;
  name: string;
  location: string | null;
  shape?: "line" | "pile" | null;
  latitude: number | null;
  longitude: number | null;
  end_latitude: number | null;
  end_longitude: number | null;
};

const GOOGLE_MAPS_API_KEY = "AIzaSyDZWH9JxBDTkBOGkBhwV7Hjx1F45eTyYIo";
const DEFAULT_CENTER = { lat: 34.178467, lng: -112.788317 };

function formatCoord(value: number | null | undefined) {
  return typeof value === "number" ? value.toFixed(6) : "Not set";
}

function formatAccuracy(value: number | null | undefined) {
  return typeof value === "number" ? `+/-${Math.round(value)} ft` : "accuracy unknown";
}

function feetBetween(a: Point, b: Point) {
  const toRad = (v: number) => (v * Math.PI) / 180;
  const earthFeet = 20902231;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * earthFeet * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

function midpoint(a: Point, b: Point): Point {
  return { lat: (a.lat + b.lat) / 2, lng: (a.lng + b.lng) / 2 };
}

function oppositeEdge(center: Point, edge: Point): Point {
  return { lat: center.lat * 2 - edge.lat, lng: center.lng * 2 - edge.lng };
}

function getGpsOnce(): Promise<GpsPoint> {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("GPS is not available on this device."));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          accuracy: position.coords.accuracy ? position.coords.accuracy * 3.28084 : null,
        });
      },
      (error) => {
        if (error.code === error.PERMISSION_DENIED) {
          reject(new Error("Location permission denied. Enable location access, or place the pins manually."));
        } else if (error.code === error.TIMEOUT) {
          reject(new Error("GPS timed out. Place the pin manually or try again outside."));
        } else {
          reject(new Error("GPS signal unavailable. Place the pin manually or try again outside."));
        }
      },
      { enableHighAccuracy: true, maximumAge: 0, timeout: 6500 }
    );
  });
}

async function getBestGpsPoint(samples = 3) {
  const readings: GpsPoint[] = [];

  for (let i = 0; i < samples; i += 1) {
    try {
      readings.push(await getGpsOnce());
    } catch (error: any) {
      if (error?.message?.includes("permission")) throw error;
      if (i === samples - 1 && readings.length === 0) throw error;
    }
  }

  readings.sort((a, b) => (a.accuracy ?? Infinity) - (b.accuracy ?? Infinity));
  const best = readings.slice(0, Math.min(2, readings.length));
  return {
    lat: best.reduce((sum, p) => sum + p.lat, 0) / best.length,
    lng: best.reduce((sum, p) => sum + p.lng, 0) / best.length,
    accuracy: readings[0].accuracy,
  };
}

export default function OperatorWindrowLocation() {
  const { id } = useParams<{ id: string }>();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [windrow, setWindrow] = useState<Windrow | null>(null);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState<"line" | "pile">("line");
  const [selectedPin, setSelectedPin] = useState<"a" | "b">("a");
  const [pointA, setPointA] = useState<Point | null>(null);
  const [pointB, setPointB] = useState<Point | null>(null);
  const [liveGps, setLiveGps] = useState<GpsPoint | null>(null);
  const [capturing, setCapturing] = useState<"a" | "b" | null>(null);
  const [watching, setWatching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const watchIdRef = useRef<number | null>(null);
  const { isLoaded } = useLoadScript({ googleMapsApiKey: GOOGLE_MAPS_API_KEY });

  useEffect(() => {
    apiFetch(`/api/windrows/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => {
        if (!r.ok) throw new Error("Not found");
        return r.json();
      })
      .then((json) => {
        const next = json.windrow as Windrow;
        const nextMode = next.shape === "pile" ? "pile" : "line";
        const hasStart = next.latitude != null && next.longitude != null;
        const hasEnd = next.end_latitude != null && next.end_longitude != null;
        setWindrow(next);
        setMode(nextMode);

        if (hasStart && hasEnd && nextMode === "pile") {
          const center = { lat: next.latitude!, lng: next.longitude! };
          const edgeB = { lat: next.end_latitude!, lng: next.end_longitude! };
          setPointA(oppositeEdge(center, edgeB));
          setPointB(edgeB);
        } else {
          if (hasStart) setPointA({ lat: next.latitude!, lng: next.longitude! });
          if (hasEnd) setPointB({ lat: next.end_latitude!, lng: next.end_longitude! });
        }
      })
      .catch(() => navigate("/app/operator"))
      .finally(() => setLoading(false));
  }, [id, token, navigate]);

  useEffect(() => {
    return () => {
      if (watchIdRef.current != null && navigator.geolocation) {
        navigator.geolocation.clearWatch(watchIdRef.current);
      }
    };
  }, []);

  const center = useMemo(() => {
    if (liveGps) return { lat: liveGps.lat, lng: liveGps.lng };
    if (pointA && pointB) return midpoint(pointA, pointB);
    return pointA || pointB || DEFAULT_CENTER;
  }, [liveGps, pointA, pointB]);

  const lengthFeet = pointA && pointB ? feetBetween(pointA, pointB) : null;
  const pileCenter = mode === "pile" && pointA && pointB ? midpoint(pointA, pointB) : null;
  const pileRadiusFeet = mode === "pile" && lengthFeet != null ? lengthFeet / 2 : null;
  const canSave = Boolean(pointA && pointB);

  const setPin = (pin: "a" | "b", point: Point) => {
    if (pin === "a") setPointA(point);
    else setPointB(point);
  };

  const startLiveGps = () => {
    if (!navigator.geolocation) {
      setMessage({ type: "error", text: "GPS is not available on this device. Tap the map to place pins manually." });
      return;
    }
    if (watchIdRef.current != null) {
      if (liveGps) mapRef.current?.panTo(liveGps);
      return;
    }

    setWatching(true);
    setMessage({ type: "success", text: "GPS started. You can still drag pins to correct accuracy." });
    watchIdRef.current = navigator.geolocation.watchPosition(
      (position) => {
        const next = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          accuracy: position.coords.accuracy ? position.coords.accuracy * 3.28084 : null,
        };
        setLiveGps(next);
        mapRef.current?.panTo(next);
      },
      (error) => {
        setWatching(false);
        if (watchIdRef.current != null) navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
        setMessage({
          type: "error",
          text:
            error.code === error.PERMISSION_DENIED
              ? "Location permission denied. Tap the map or drag pins manually."
              : "GPS signal unavailable. Tap the map or drag pins manually.",
        });
      },
      { enableHighAccuracy: true, maximumAge: 0, timeout: 6500 }
    );
  };

  const captureGps = async (pin: "a" | "b") => {
    setCapturing(pin);
    setMessage({ type: "success", text: `Capturing Point ${pin.toUpperCase()}...` });
    try {
      const gps = await getBestGpsPoint();
      setLiveGps(gps);
      setPin(pin, gps);
      setSelectedPin(pin === "a" ? "b" : "a");
      mapRef.current?.panTo(gps);
      setMessage({ type: "success", text: `Point ${pin.toUpperCase()} set from GPS (${formatAccuracy(gps.accuracy)}). Drag it if needed.` });
    } catch (error: any) {
      setMessage({ type: "error", text: error?.message || "GPS failed. Tap the map to place the pin manually." });
    } finally {
      setCapturing(null);
    }
  };

  const save = async () => {
    if (!pointA || !pointB || !windrow) {
      setMessage({ type: "error", text: "Set Point A and Point B before saving." });
      return;
    }

    const payload =
      mode === "pile"
        ? {
            shape: "pile",
            latitude: midpoint(pointA, pointB).lat,
            longitude: midpoint(pointA, pointB).lng,
            end_latitude: pointB.lat,
            end_longitude: pointB.lng,
          }
        : {
            shape: "line",
            latitude: pointA.lat,
            longitude: pointA.lng,
            end_latitude: pointB.lat,
            end_longitude: pointB.lng,
          };

    setSaving(true);
    setMessage(null);
    try {
      const res = await apiFetch(`/api/windrows/${windrow.id}`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || "Failed to save GPS map");
      navigate(`/app/operator/windrows/${windrow.id}`);
    } catch (error: any) {
      setMessage({ type: "error", text: error?.message || "Could not save GPS map." });
    } finally {
      setSaving(false);
    }
  };

  if (loading || !windrow) {
    return (
      <div className="space-y-4">
        <div className="h-6 bg-muted rounded w-32 animate-pulse" />
        <div className="h-[520px] bg-card border border-border rounded-2xl animate-pulse" />
      </div>
    );
  }

  return (
    <div className="space-y-4 pb-4">
      <div className="flex items-center justify-between gap-3">
        <Link
          to={`/app/operator/windrows/${windrow.id}`}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft size={14} />
          Back
        </Link>
        <button
          type="button"
          onClick={save}
          disabled={!canSave || saving}
          className="inline-flex items-center justify-center gap-2 h-10 px-4 rounded-xl bg-forest-600 text-white text-sm font-bold disabled:opacity-60"
        >
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
          Save
        </button>
      </div>

      <div className="bg-card border border-border rounded-2xl overflow-hidden">
        <div className="p-4 border-b border-border">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">GPS Mapping</p>
          <h1 className="heading-display text-2xl text-foreground mt-1">{windrow.name}</h1>
          <p className="text-xs text-muted-foreground mt-1">
            {windrow.row_id}{windrow.location ? ` · ${windrow.location}` : ""}
          </p>
        </div>

        <div className="p-4 space-y-4">
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setMode("line")}
              className={`min-h-[52px] rounded-xl border px-3 text-left transition ${
                mode === "line"
                  ? "border-forest-500 bg-forest-50 text-forest-800 dark:bg-forest-900/20 dark:text-forest-300"
                  : "border-border bg-secondary/50 text-muted-foreground"
              }`}
            >
              <Route size={16} />
              <p className="text-sm font-bold mt-1">Windrow line</p>
            </button>
            <button
              type="button"
              onClick={() => setMode("pile")}
              className={`min-h-[52px] rounded-xl border px-3 text-left transition ${
                mode === "pile"
                  ? "border-earth-500 bg-earth-50 text-earth-800 dark:bg-earth-900/20 dark:text-earth-300"
                  : "border-border bg-secondary/50 text-muted-foreground"
              }`}
            >
              <CircleDot size={16} />
              <p className="text-sm font-bold mt-1">Pile circle</p>
            </button>
          </div>

          <div className="rounded-2xl overflow-hidden border border-border bg-muted/30 h-[min(52vh,520px)] min-h-[340px]">
            {isLoaded ? (
              <GoogleMap
                center={center}
                zoom={19}
                onLoad={(map) => {
                  mapRef.current = map;
                }}
                onClick={(event) => {
                  if (!event.latLng) return;
                  setPin(selectedPin, { lat: event.latLng.lat(), lng: event.latLng.lng() });
                  setSelectedPin(selectedPin === "a" ? "b" : "a");
                }}
                mapContainerStyle={{ width: "100%", height: "100%" }}
                options={{
                  mapTypeId: "satellite",
                  fullscreenControl: false,
                  streetViewControl: false,
                  mapTypeControl: false,
                  clickableIcons: false,
                }}
              >
                {liveGps && (
                  <>
                    <MarkerF position={liveGps} label="You" />
                    {liveGps.accuracy != null && (
                      <CircleF
                        center={liveGps}
                        radius={liveGps.accuracy / 3.28084}
                        options={{ fillColor: "#2563eb", fillOpacity: 0.12, strokeColor: "#2563eb", strokeOpacity: 0.45, strokeWeight: 1 }}
                      />
                    )}
                  </>
                )}
                {pointA && (
                  <MarkerF
                    position={pointA}
                    label="A"
                    draggable
                    onDragEnd={(event) => event.latLng && setPointA({ lat: event.latLng.lat(), lng: event.latLng.lng() })}
                  />
                )}
                {pointB && (
                  <MarkerF
                    position={pointB}
                    label="B"
                    draggable
                    onDragEnd={(event) => event.latLng && setPointB({ lat: event.latLng.lat(), lng: event.latLng.lng() })}
                  />
                )}
                {mode === "line" && pointA && pointB && (
                  <PolylineF path={[pointA, pointB]} options={{ strokeColor: "#2d7330", strokeOpacity: 0.95, strokeWeight: 5 }} />
                )}
                {mode === "pile" && pileCenter && pileRadiusFeet != null && pointA && pointB && (
                  <>
                    <PolylineF path={[pointA, pointB]} options={{ strokeColor: "#c8902a", strokeOpacity: 0.95, strokeWeight: 4 }} />
                    <CircleF
                      center={pileCenter}
                      radius={pileRadiusFeet / 3.28084}
                      options={{ fillColor: "#c8902a", fillOpacity: 0.18, strokeColor: "#c8902a", strokeOpacity: 0.95, strokeWeight: 3 }}
                    />
                  </>
                )}
              </GoogleMap>
            ) : (
              <div className="h-full flex items-center justify-center text-sm text-muted-foreground">Loading map...</div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setSelectedPin("a")}
              className={`rounded-xl border px-3 py-2 text-left ${selectedPin === "a" ? "border-forest-500 bg-forest-50 dark:bg-forest-900/20" : "border-border bg-secondary/50"}`}
            >
              <p className="text-[10px] uppercase tracking-wide font-bold text-muted-foreground">Tap places</p>
              <p className="text-sm font-bold text-foreground">Point A</p>
            </button>
            <button
              type="button"
              onClick={() => setSelectedPin("b")}
              className={`rounded-xl border px-3 py-2 text-left ${selectedPin === "b" ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20" : "border-border bg-secondary/50"}`}
            >
              <p className="text-[10px] uppercase tracking-wide font-bold text-muted-foreground">Tap places</p>
              <p className="text-sm font-bold text-foreground">Point B</p>
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <button
              type="button"
              onClick={startLiveGps}
              className="min-h-[44px] rounded-xl border border-border bg-secondary text-sm font-bold text-foreground inline-flex items-center justify-center gap-2"
            >
              <Navigation size={16} className={watching ? "text-forest-600 dark:text-forest-400" : "text-muted-foreground"} />
              {liveGps ? `Center (${formatAccuracy(liveGps.accuracy)})` : "Start GPS"}
            </button>
            <button
              type="button"
              onClick={() => captureGps("a")}
              disabled={capturing != null}
              className="min-h-[44px] rounded-xl border border-forest-200 bg-forest-50 text-sm font-bold text-forest-800 dark:border-forest-800 dark:bg-forest-900/20 dark:text-forest-300 inline-flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {capturing === "a" ? <Loader2 size={16} className="animate-spin" /> : <Crosshair size={16} />}
              GPS to A
            </button>
            <button
              type="button"
              onClick={() => captureGps("b")}
              disabled={capturing != null}
              className="min-h-[44px] rounded-xl border border-blue-200 bg-blue-50 text-sm font-bold text-blue-800 dark:border-blue-800 dark:bg-blue-900/20 dark:text-blue-300 inline-flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {capturing === "b" ? <Loader2 size={16} className="animate-spin" /> : <Crosshair size={16} />}
              GPS to B
            </button>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div className="rounded-xl bg-secondary/60 px-3 py-2">
              <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">{mode === "pile" ? "Radius / diameter" : "Length"}</p>
              <p className="text-sm font-bold text-foreground">
                {mode === "pile"
                  ? pileRadiusFeet != null
                    ? `${Math.round(pileRadiusFeet)} / ${Math.round(pileRadiusFeet * 2)} ft`
                    : "Need A + B"
                  : lengthFeet != null
                    ? `${Math.round(lengthFeet)} ft`
                    : "Need A + B"}
              </p>
            </div>
            <div className="rounded-xl bg-secondary/60 px-3 py-2">
              <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">Precision</p>
              <p className="text-sm font-bold text-foreground">Drag pins before save</p>
            </div>
          </div>

          <div className="rounded-xl border border-border bg-background/70 px-3 py-2 text-[11px] text-muted-foreground flex items-start gap-2">
            <MousePointer2 size={14} className="mt-0.5 shrink-0" />
            <span>Use GPS to get close. Then tap the map or drag Point A and Point B until the shape matches the real windrow or pile.</span>
          </div>

          {message && (
            <div
              className={`rounded-xl px-3 py-2 text-[11px] font-medium ${
                message.type === "success"
                  ? "bg-forest-50 text-forest-700 border border-forest-200 dark:bg-forest-900/20 dark:text-forest-300 dark:border-forest-800"
                  : "bg-red-50 text-red-700 border border-red-200 dark:bg-red-900/20 dark:text-red-300 dark:border-red-800"
              }`}
            >
              {message.text}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div className="rounded-xl bg-card border border-border px-3 py-2">
          <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">Point A</p>
          <p className="text-xs font-bold text-foreground truncate">
            {pointA ? `${formatCoord(pointA.lat)}, ${formatCoord(pointA.lng)}` : "Not set"}
          </p>
        </div>
        <div className="rounded-xl bg-card border border-border px-3 py-2">
          <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">Point B</p>
          <p className="text-xs font-bold text-foreground truncate">
            {pointB ? `${formatCoord(pointB.lat)}, ${formatCoord(pointB.lng)}` : "Not set"}
          </p>
        </div>
      </div>
    </div>
  );
}
